import { useQuery, useMutation, useQueryClient } from 'react-query';
import Layout from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/Card';
import { tenantClientPortalMessagesAPI } from '../../api';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { format } from 'date-fns';

export default function TenantClientPortalMessages() {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['tenant-client-portal-messages'],
    queryFn: () => tenantClientPortalMessagesAPI.list({ limit: 80 }),
  });

  const markViewed = useMutation({
    mutationFn: (id) => tenantClientPortalMessagesAPI.markViewed(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['tenant-client-portal-messages']);
      queryClient.invalidateQueries(['dashboard-stats']);
    },
  });

  const messages = data?.messages ?? [];

  const openRow = (m) => {
    setExpanded(m.id === expanded ? null : m.id);
    if (!m.tenantFirstViewedAt) {
      markViewed.mutate(m.id);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-6 py-8">
        <h1 className="text-display font-bold text-[var(--ds-text-primary)] mb-2">Client portal messages</h1>
        <p className="text-meta text-[var(--ds-text-secondary)] mb-6">
          Messages sent by clients from their portal. New messages are highlighted until opened.
        </p>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-10 w-10 animate-spin text-[var(--ds-text-muted)]" />
          </div>
        ) : messages.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-[var(--ds-text-muted)]">No messages yet.</CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {messages.map((m) => (
              <Card
                key={m.id}
                className={`cursor-pointer border ${!m.tenantFirstViewedAt ? 'border-amber-400/60 bg-amber-50/40 dark:bg-amber-950/20' : ''}`}
              >
                <CardContent className="p-4" onClick={() => openRow(m)} onKeyDown={(e) => e.key === 'Enter' && openRow(m)} role="button" tabIndex={0}>
                  <div className="flex flex-wrap justify-between gap-2">
                    <div>
                      <p className="font-medium text-[var(--ds-text-primary)]">{m.reason}</p>
                      <p className="text-sm text-[var(--ds-text-secondary)]">
                        {m.clientDisplayName}
                        {m.contactName ? ` · ${m.contactName}` : ''}
                      </p>
                    </div>
                    <div className="text-xs text-[var(--ds-text-muted)]">
                      {m.createdAt ? format(new Date(m.createdAt), 'dd MMM yyyy HH:mm') : ''}
                      {!m.tenantFirstViewedAt && (
                        <span className="ml-2 text-amber-700 dark:text-amber-400 font-medium">New</span>
                      )}
                    </div>
                  </div>
                  {expanded === m.id && (
                    <div className="mt-4 pt-4 border-t border-[var(--ds-border-subtle)] space-y-2">
                      <p className="text-sm">
                        <span className="text-[var(--ds-text-muted)]">Preferred contact: </span>
                        {m.contactMethod}
                      </p>
                      <p className="text-sm whitespace-pre-wrap text-[var(--ds-text-primary)]">{m.message}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
